'use client';

import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
  ReactElement,
} from 'react';

import { apiGet } from '@/lib/api/client';
import {
  COOKIE_KEYS,
  STORAGE_KEYS,
  getCookie,
  getStoredNumber,
  setStoredNumber,
} from '@/lib/utils/storage';

// /auth/me のレスポンス(組織ID解決に必要な項目のみ)
interface MeResponse {
  OrganizationID: number | null;
}

interface OrganizationContextType {
  organizationId: number | null;
  setOrganizationId: React.Dispatch<React.SetStateAction<number | null>>;
}

const defaultContextValue: OrganizationContextType = {
  organizationId: null,
  setOrganizationId: () => {
    console.warn('setOrganizationId called before initialization');
  },
};

const OrganizationContext = createContext<OrganizationContextType>(defaultContextValue);

export const OrganizationProvider = ({ children }: { children: ReactNode }): ReactElement => {
  const [organizationId, setOrganizationId] = useState<number | null>(null);

  // クライアントマウント後に組織IDを解決する。
  // organizationID Cookieはhttp-only(JSから読めない)ため、確定値は/auth/meで取得する。
  // Cookie/localStorageは即時表示用の後方互換フォールバック。
  useEffect(() => {
    // 1) 即時フォールバック(Cookie→localStorage)
    const cookieOrgId = getCookie(COOKIE_KEYS.ORGANIZATION_ID);
    if (cookieOrgId) {
      const orgId = parseInt(cookieOrgId, 10);
      setOrganizationId(orgId);
      setStoredNumber(STORAGE_KEYS.ORGANIZATION_ID, orgId);
    } else {
      const saved = getStoredNumber(STORAGE_KEYS.ORGANIZATION_ID);
      if (saved !== null) {
        setOrganizationId(saved);
      }
    }

    // 2) サーバから確定値を取得(認証Cookieはcredentials:'include'で送信される)
    (async () => {
      const result = await apiGet<MeResponse>('/auth/me', { skipAuthRedirect: true });
      if (result.data?.OrganizationID != null) {
        setOrganizationId(result.data.OrganizationID);
        setStoredNumber(STORAGE_KEYS.ORGANIZATION_ID, result.data.OrganizationID);
      }
    })();
  }, []);

  // 変更があったら保存
  useEffect(() => {
    if (organizationId !== null) {
      setStoredNumber(STORAGE_KEYS.ORGANIZATION_ID, organizationId);
    }
  }, [organizationId]);

  return (
    <OrganizationContext.Provider value={{ organizationId, setOrganizationId }}>
      {children}
    </OrganizationContext.Provider>
  );
};

export const useOrganizationId = (): OrganizationContextType => useContext(OrganizationContext);
